import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import { Calculator, AlertTriangle, Package, Truck, Plus, Trash2, MessageCircle, Loader2, FileText, Receipt, ExternalLink } from "lucide-react";

const PRICE_MAP = {
  "T-Shirt Adulti": { price: 5.0 },
  "T-Shirt Bambini": { price: 5.0 },
  "T-shirt": { price: 5.0 },
  "Polo": { price: 10.0 },
  "Felpe con Zip e Cappuccio": { price: 20.0 },
  "Felpa con Zip e Cappuccio": { price: 20.0 },
  "Felpe con Cappuccio Senza Zip": { price: 20.0 },
  "Felpa con Cappuccio Senza Zip": { price: 20.0 },
  "Canotta in Cotone": { price: 6.0 },
  "Felpa Zip": { price: 20.0 },
  "Canotte in Cotone": { price: 6.0 },
  "Canotta": { price: 6.0 },
  "Body Baby": { price: 5.0 },
  "Body Bambini": { price: 5.0 },
  "Cappello Pescatore": { price: 3.5, hat: true },
  "Cappello": { price: 3.5, hat: true },
  "Berretti": { price: 3.5, hat: true },
  "Berretto": { price: 3.5, hat: true },
};
const DEFAULT_PRICE = 5.0;

const HAT_PRINTS = [
  { id: "fronte", label: "Solo Fronte", price: 2.5 },
  { id: "retro", label: "Solo retro", price: 2.5 },
  { id: "frontretro", label: "Fronte/Retro", price: 5.0 },
];
const PRINTS = [
  { id: "cuore", label: "Lato cuore", price: 2.5 },
  { id: "fronte", label: "Stampa completa fronte", price: 5.0 },
  { id: "retro", label: "Stampa completa retro", price: 5.0 },
  { id: "frontretro", label: "Stampa completa F/R", price: 10.0 },
];

const ZONE = { napoli: { label: "Napoli e Provincia", costo: 3.5 }, italia: { label: "Resto d'Italia", costo: 6.0 }, ritiro: { label: "Ritiro di persona", costo: 0 } };
const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;

export default function Calcolatore() {
  const [items, setItems] = useState([]);
  const [garmentIdx, setGarmentIdx] = useState(0);
  const [colore, setColore] = useState("");
  const [taglia, setTaglia] = useState("");
  const [printId, setPrintId] = useState(PRINTS[0].id);
  const [plus, setPlus] = useState(false);
  const [plusQty, setPlusQty] = useState(1);
  const [quantita, setQuantita] = useState(1);
  const [righe, setRighe] = useState([]);
  const [zona, setZona] = useState("napoli");
  const [whatsapp, setWhatsapp] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [bozzaImporto, setBozzaImporto] = useState("5");
  const [bozzaLoading, setBozzaLoading] = useState(false);
  const [bozzaResult, setBozzaResult] = useState(null);
  const [scalaBozza, setScalaBozza] = useState(false);

  useEffect(() => { api.get("/magazzino").then(({ data }) => setItems(data)).catch(() => {}); }, []);

  const garments = useMemo(() => {
    const tipi = [...new Set(items.map((i) => i.tipo_capo).filter(Boolean))].sort();
    return tipi.map((t) => ({ label: t, tipo: t, price: PRICE_MAP[t]?.price ?? DEFAULT_PRICE, hat: !!PRICE_MAP[t]?.hat }));
  }, [items]);
  useEffect(() => { if (garments.length && garmentIdx >= garments.length) setGarmentIdx(0); }, [garments]); // eslint-disable-line
  const garment = garments[garmentIdx] || garments[0] || { label: "", tipo: "", price: 0, hat: false };
  const activePrints = garment.hat ? HAT_PRINTS : PRINTS;
  const grafica = activePrints.find((p) => p.id === printId) || activePrints[0];
  const plusCount = plus ? Math.max(1, parseInt(plusQty, 10) || 1) : 0;
  const prezzoUnitario = garment.price + (grafica?.price || 0) + plusCount * 3;

  const matched = useMemo(() => items.filter((s) => (s.tipo_capo || "") === garment.tipo), [items, garment.tipo]);
  const colori = useMemo(() => [...new Set(matched.map((m) => m.colore))].sort(), [matched]);
  const taglie = useMemo(() => [...new Set(matched.filter((m) => m.colore === colore).map((m) => m.taglia))], [matched, colore]);
  useEffect(() => { setColore(colori.length ? (colori.includes(colore) ? colore : colori[0]) : ""); }, [colori]); // eslint-disable-line
  useEffect(() => { setTaglia(taglie.length ? (taglie.includes(taglia) ? taglia : taglie[0]) : ""); }, [taglie]); // eslint-disable-line

  const selectedItem = useMemo(() => matched.find((m) => m.colore === colore && m.taglia === taglia) || null, [matched, colore, taglia]);
  const disponibile = selectedItem ? selectedItem.quantita_attuale || 0 : 0;
  const impegnato = righe.filter((r) => r.sku_neutro === selectedItem?.sku_neutro).reduce((a, r) => a + r.quantita, 0);
  const qty = Math.max(1, parseInt(quantita, 10) || 1);
  const insufficiente = selectedItem && qty > disponibile - impegnato;

  const alternative = useMemo(() => {
    return matched
      .filter((m) => m.taglia === taglia && m.colore !== colore && (m.quantita_attuale || 0) > 0)
      .sort((a, b) => (b.quantita_attuale || 0) - (a.quantita_attuale || 0))
      .slice(0, 12);
  }, [matched, colore, taglia]);

  const addRiga = () => {
    if (!selectedItem) return toast.error("Seleziona un colore e una taglia disponibili.");
    if (insufficiente) return toast.error("Scorte insufficienti per questa variante.");
    const descr = `${qty} x ${garment.label} ${colore} ${taglia} · ${grafica.label}${plusCount ? ` · ${plusCount} Stampe Plus` : ""}`;
    setRighe((prev) => [...prev, { sku_neutro: selectedItem.sku_neutro, tipo_capo: garment.label, colore, taglia, grafica: grafica.label, stampe_plus: plusCount, quantita: qty, prezzo_unitario: prezzoUnitario, descrizione: descr }]);
    setQuantita(1); setPlus(false); setPlusQty(1);
    toast.success("Articolo aggiunto al preventivo.");
  };
  const removeRiga = (i) => setRighe((prev) => prev.filter((_, idx) => idx !== i));

  const subtotale = righe.reduce((a, r) => a + r.prezzo_unitario * r.quantita, 0);
  const isRitiro = zona === "ritiro";
  const spedGratis = !isRitiro && subtotale > 39;
  const spedizione = isRitiro || spedGratis ? 0 : ZONE[zona].costo;
  const totale = subtotale + spedizione;
  const bozzaScalata = scalaBozza ? Math.max(0, parseFloat(String(bozzaImporto).replace(",", ".")) || 0) : 0;
  const totaleFinale = Math.max(0, totale - bozzaScalata);

  const buildQuoteText = () => {
    const lines = righe.map((r) => `• ${r.quantita}× ${r.tipo_capo} ${r.colore} ${r.taglia}${r.grafica ? " · " + r.grafica : ""}${r.stampe_plus ? ` · ${r.stampe_plus} Stampe Plus` : ""}: ${eur(r.prezzo_unitario * r.quantita)}`);
    const sped = spedizione === 0 ? (isRitiro ? "Ritiro di persona (gratis)" : "Spedizione gratuita") : `Spedizione: ${eur(spedizione)}`;
    const extra = bozzaScalata > 0 ? `\nBozza già pagata: -${eur(bozzaScalata)}` : "";
    const totLabel = bozzaScalata > 0 ? "Totale da saldare" : "Totale";
    return `Ecco a te il tuo preventivo Gigli Edition:\n\n${lines.join("\n")}\n\n${sped}${extra}\n${totLabel}: ${eur(totaleFinale)}\n\nFammi sapere se confermi e procediamo! 😊`;
  };

  const salvaEInvia = async () => {
    if (righe.length === 0) return toast.error("Aggiungi almeno un articolo al preventivo.");
    const tel = whatsapp.replace(/\D/g, "");
    const waWindow = tel.length >= 8 ? window.open("", "_blank") : null;
    setSaving(true);
    try {
      await api.post("/preventivi", { righe, zona, whatsapp: tel || null, note: note || null });
      if (waWindow && tel.length >= 8) waWindow.location.href = `https://wa.me/${tel}?text=${encodeURIComponent(buildQuoteText())}`;
      toast.success(`Preventivo salvato${tel.length >= 8 ? " e inviato su WhatsApp" : ""}.`);
      setRighe([]); setNote(""); setWhatsapp("");
    } catch (e) {
      if (waWindow) waWindow.close();
      toast.error(e.response?.data?.detail || "Errore nel salvataggio del preventivo.");
    } finally { setSaving(false); }
  };

  const creaBozza = async () => {
    const imp = parseFloat(String(bozzaImporto).replace(",", "."));
    if (!(imp > 0)) return toast.error("Inserisci un importo bozza valido.");
    const tel = whatsapp.replace(/\D/g, "");
    const waWindow = tel.length >= 8 ? window.open("", "_blank") : null;
    setBozzaLoading(true);
    setBozzaResult(null);
    try {
      const { data } = await api.post("/shopify/draft-order", {
        righe: [{ descrizione: "Bozza personalizzazione Gigli Edition", prezzo_unitario: imp, quantita: 1 }],
        zona: "ritiro", whatsapp: tel || null,
      });
      const invoiceUrl = data.invoice_url;
      if (!invoiceUrl) throw new Error("Nessun link di pagamento ricevuto da Shopify.");
      const msg = `Ecco a te il link per pagare la bozza di ${eur(imp)} e avviare la lavorazione del tuo ordine:\n\n${invoiceUrl}\n\n💶 L'importo della bozza verrà sottratto dal totale al completamento dell'ordine.\n\n📧 In fase di pagamento inserisci la tua email per ricevere gli aggiornamenti.\n\nGrazie dallo staff Gigli Edition!`;
      const waUrl = tel.length >= 8 ? `https://wa.me/${tel}?text=${encodeURIComponent(msg)}` : null;
      setBozzaResult({ invoiceUrl, waUrl });
      if (waWindow && waUrl) waWindow.location.href = waUrl;
      toast.success("Link bozza creato.");
    } catch (e) {
      if (waWindow) waWindow.close();
      toast.error(e.response?.data?.detail || e.message || "Errore nella creazione del link bozza.");
    } finally { setBozzaLoading(false); }
  };

  const inputCls = "mt-1 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 text-sm focus:border-cyan-400/60 outline-none disabled:opacity-50";
  const labelCls = "text-xs uppercase tracking-wider text-slate-400 font-semibold";

  return (
    <Layout>
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-100 tracking-tight">Preventivatore</h1>
            <p className="text-slate-500 text-sm">Crea un preventivo con più articoli e invialo su WhatsApp per l'approvazione</p>
          </div>
        </div>
        <Link data-testid="link-preventivi" to="/preventivi" className="inline-flex items-center gap-2 text-sm text-cyan-300 hover:text-cyan-200 border border-cyan-400/30 rounded-lg px-3 py-2">
          <FileText className="w-4 h-4" /> Vai ai Preventivi
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Editor articolo */}
        <div className="glass rounded-xl p-5 space-y-4">
          <h3 className="font-heading text-lg font-semibold text-slate-100">Aggiungi articolo</h3>
          <div>
            <label className={labelCls}>Capo</label>
            <select data-testid="garment-select" value={garmentIdx} onChange={(e) => setGarmentIdx(Number(e.target.value))} className={inputCls}>
              {garments.map((g, i) => (<option key={g.label} value={i}>{g.label}</option>))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Colore</label>
              <select data-testid="colore-select" value={colore} onChange={(e) => setColore(e.target.value)} disabled={!colori.length} className={inputCls}>
                {colori.length === 0 && <option value="">—</option>}
                {colori.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Taglia</label>
              <select data-testid="taglia-select" value={taglia} onChange={(e) => setTaglia(e.target.value)} disabled={!taglie.length} className={inputCls}>
                {taglie.length === 0 && <option value="">—</option>}
                {taglie.map((t) => (<option key={t} value={t}>{t}</option>))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Grafica / Posizione</label>
            <select data-testid="grafica-select" value={grafica.id} onChange={(e) => setPrintId(e.target.value)} className={inputCls}>
              {activePrints.map((p) => (<option key={p.id} value={p.id}>{p.label}</option>))}
            </select>
          </div>
          <div className="flex items-center gap-3 bg-slate-900/40 border border-slate-700/60 rounded-lg px-3 py-2.5">
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" data-testid="plus-check" checked={plus} onChange={(e) => setPlus(e.target.checked)} className="accent-cyan-400 w-4 h-4" />
              Stampe Plus
            </label>
            <input data-testid="plus-qty" type="number" min="1" value={plusQty} onChange={(e) => setPlusQty(e.target.value)} disabled={!plus}
              className="w-20 bg-slate-900/60 border border-slate-700 rounded-lg px-2 py-1.5 text-slate-100 text-sm font-mono-tech disabled:opacity-40" />
            <span className="text-xs text-slate-500 ml-auto text-right">maniche/polsi/tasche · 3€ cad.</span>
          </div>
          <div>
            <label className={labelCls}>Quantità</label>
            <input data-testid="quantita-input" type="number" min="1" value={quantita} onChange={(e) => setQuantita(e.target.value)} className={`${inputCls} font-mono-tech`} />
          </div>
          <div className="flex items-center justify-between bg-slate-900/40 border border-slate-700/60 rounded-lg px-3 py-2.5">
            <span className="text-sm text-slate-400">Prezzo unitario</span>
            <span data-testid="prezzo-unitario" className="font-mono-tech text-cyan-300 font-bold">{eur(prezzoUnitario)}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="stock-label">
            <Package className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-300">Disponibili: <strong className="text-slate-100">{disponibile - impegnato}</strong> pz{selectedItem && <span className="text-slate-500"> ({selectedItem.sku_neutro})</span>}</span>
          </div>
          {insufficiente && (
            <div className="space-y-2" data-testid="stock-warning-wrap">
              <div data-testid="stock-warning" className="inline-flex items-center gap-2 bg-red-500/15 border border-red-500/40 text-red-300 rounded-lg px-3 py-2 text-sm font-semibold">
                <AlertTriangle className="w-4 h-4" /> Scorte insufficienti!
              </div>
              {alternative.length > 0 && (
                <div data-testid="stock-alternatives">
                  <p className="text-xs text-slate-400 mb-1.5">Altri colori disponibili per la taglia {taglia} (clicca):</p>
                  <div className="flex flex-wrap gap-1.5">
                    {alternative.map((m) => (
                      <button key={m.sku_neutro} type="button" data-testid={`alt-${m.sku_neutro}`} onClick={() => setColore(m.colore)}
                        className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-cyan-400/15 border border-slate-700 hover:border-cyan-400/40 rounded-lg px-2.5 py-1.5 text-xs text-slate-200 transition-colors">
                        {m.colore} <span className="text-emerald-300 font-mono-tech">{m.quantita_attuale} pz</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <button data-testid="add-riga-btn" onClick={addRiga} disabled={!selectedItem || insufficiente} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Plus className="w-4 h-4" /> Aggiungi al preventivo
          </button>
        </div>

        {/* Riepilogo preventivo */}
        <div className="glass rounded-xl p-5 space-y-4 h-fit">
          <h3 className="font-heading text-lg font-semibold text-slate-100">Riepilogo Preventivo</h3>
          <div data-testid="righe-list" className="space-y-2">
            {righe.length === 0 && <p className="text-sm text-slate-500">Nessun articolo aggiunto. Compila a sinistra e clicca "Aggiungi al preventivo".</p>}
            {righe.map((r, i) => (
              <div key={i} data-testid={`riga-${i}`} className="flex items-center gap-2 bg-slate-900/40 border border-slate-700/60 rounded-lg px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-200 truncate">{r.quantita}× {r.tipo_capo} {r.colore} {r.taglia}</div>
                  <div className="text-xs text-slate-500">{r.grafica} · {r.sku_neutro} · {eur(r.prezzo_unitario)}/pz</div>
                </div>
                <span className="font-mono-tech text-slate-100 text-sm">{eur(r.prezzo_unitario * r.quantita)}</span>
                <button data-testid={`remove-riga-${i}`} onClick={() => removeRiga(i)} className="w-8 h-8 shrink-0 rounded-lg bg-slate-800 text-slate-400 hover:text-red-300 hover:bg-red-500/15 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          <div>
            <label className={labelCls}>Zona di spedizione</label>
            <select data-testid="zona-select" value={zona} onChange={(e) => setZona(e.target.value)} className={inputCls}>
              <option value="napoli">Napoli e Provincia (3,50 €)</option>
              <option value="italia">Resto d'Italia (6,00 €)</option>
              <option value="ritiro">Ritiro di persona (gratis)</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Numero WhatsApp Cliente (opzionale)</label>
            <input data-testid="whatsapp-input" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="Es. 393331234567" className={`${inputCls} font-mono-tech`} />
          </div>
          <div>
            <label className={labelCls}>Note (opzionale)</label>
            <input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. consegna entro venerdì" className={inputCls} />
          </div>

          <div className="flex items-center justify-between py-2 border-b border-slate-800/60">
            <span className="text-slate-400 text-sm">Subtotale articoli</span>
            <span data-testid="subtotale" className="font-mono-tech text-slate-200">{eur(subtotale)}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-slate-800/60">
            <span className="text-slate-400 text-sm flex items-center gap-1.5"><Truck className="w-4 h-4" /> Spedizione</span>
            <span data-testid="spedizione" className="font-mono-tech text-slate-200">{spedizione === 0 ? (isRitiro ? "RITIRO" : "GRATIS") : eur(spedizione)}</span>
          </div>
          <label className="flex items-center justify-between gap-2 bg-slate-900/40 border border-slate-700/60 rounded-lg px-3 py-2.5 cursor-pointer">
            <span className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" data-testid="scala-bozza-check" checked={scalaBozza} onChange={(e) => setScalaBozza(e.target.checked)} className="accent-amber-400 w-4 h-4" />
              Sottrai bozza già pagata
            </span>
            <input data-testid="scala-bozza-importo" type="number" step="0.5" min="0" value={bozzaImporto} onChange={(e) => setBozzaImporto(e.target.value)} disabled={!scalaBozza} className="w-24 bg-slate-900/60 border border-slate-700 rounded-lg px-2 py-1.5 text-slate-100 text-sm font-mono-tech disabled:opacity-40" />
          </label>
          {bozzaScalata > 0 && (
            <div className="flex items-center justify-between py-2 border-b border-slate-800/60" data-testid="bozza-sottratta-row">
              <span className="text-slate-400 text-sm flex items-center gap-1.5"><Receipt className="w-4 h-4" /> Bozza già pagata</span>
              <span className="font-mono-tech text-amber-300">- {eur(bozzaScalata)}</span>
            </div>
          )}
          <div className="rounded-xl bg-cyan-400/10 border border-cyan-400/30 p-4 flex items-center justify-between">
            <span className="text-cyan-200 text-sm uppercase tracking-wider font-semibold">{bozzaScalata > 0 ? "Totale da saldare" : "Totale Preventivo"}</span>
            <span data-testid="totale-preventivo" className="font-mono-tech text-3xl font-extrabold text-cyan-300">{eur(totaleFinale)}</span>
          </div>

          <button data-testid="salva-invia-btn" onClick={salvaEInvia} disabled={saving || righe.length === 0} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageCircle className="w-5 h-5" />} Salva e invia preventivo
          </button>
          <p className="text-xs text-slate-500">Il preventivo viene salvato in "Preventivi" e, se inserisci il numero, inviato su WhatsApp come testo (senza link di pagamento). Dopo l'approvazione, trasferiscilo in Vendita Rapida.</p>
        </div>
      </div>

      <div className="glass rounded-xl p-5 mt-5 space-y-4" data-testid="bozza-card">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-amber-400" />
          <h3 className="font-heading text-lg font-semibold text-slate-100">Link pagamento bozza</h3>
        </div>
        <p className="text-xs text-slate-500">Crea un link di pagamento Shopify per far pagare al cliente la bozza/acconto prima del preventivo. L'importo è modificabile.</p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="sm:w-44">
            <label className={labelCls}>Importo bozza (€)</label>
            <input data-testid="bozza-importo" type="number" step="0.5" min="0.5" value={bozzaImporto} onChange={(e) => setBozzaImporto(e.target.value)} className={`${inputCls} font-mono-tech`} />
          </div>
          <div className="flex-1">
            <label className={labelCls}>WhatsApp cliente (opzionale)</label>
            <input data-testid="bozza-whatsapp" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="Es. 393331234567" className={`${inputCls} font-mono-tech`} />
          </div>
          <button data-testid="bozza-crea-btn" onClick={creaBozza} disabled={bozzaLoading} className="py-2.5 px-4 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-2 whitespace-nowrap">
            {bozzaLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Receipt className="w-5 h-5" />} Crea link bozza
          </button>
        </div>
        {bozzaResult && (
          <div className="rounded-xl bg-slate-900/50 border border-amber-500/30 p-4 space-y-3" data-testid="bozza-result">
            <a data-testid="bozza-invoice-link" href={bozzaResult.invoiceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-cyan-300 hover:text-cyan-200 text-sm break-all">Link pagamento bozza <ExternalLink className="w-3.5 h-3.5 shrink-0" /></a>
            {bozzaResult.waUrl && <a data-testid="bozza-wa-link" href={bozzaResult.waUrl} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-sm"><MessageCircle className="w-5 h-5" /> Invia bozza su WhatsApp</a>}
          </div>
        )}
      </div>
    </Layout>
  );
}
